/**
 * Startup Error Middleware
 *
 * @XXX "nuxt4" silences `showError` and `navigateTo` execution when the
 *      lifecyle has not reached the "middleware" / "page"
 *        ↳ following delegation pattern started on `main` execution:
 *          this middleware will act as a concrete delegation executer
 *        ↳ why? bc. some parallel requests made during startup can both
 *          refresh a token (and thus hydrating the session) and in the
 *          same time throw a micro service policy infringement error
 *        ↳ the latter **takes** precedence → so in order to plainfully
 *          redirect the user (taking into account that the below line
 *          WILL NOT be applyied due to Nuxt2 silencing `redirect`),
 *          we WILL solely rely on the `auth` middleware, thus deleting
 *          defensively the session data
 *        ↳ when an error during startup occurs, a flag will be set and handled
 *          here
 */
import Console from '~/utils/lib/core/console'
import { noop, toString } from '~/utils/lib/core/prelude'
import { isPolicyInfringementError } from '~/utils/infra/common/retry-manager'
import { isOnAuthenticationPages, routeForAnonymous } from '~/utils/lib/layout/redirection'
import { useStore_core_startup } from '~/stores/core/startup'
import { useStore_layout_infringement } from '~/stores/layout/infringement'
import { useStore_account_authentication } from '~/stores/account/authentication'
import { useStore_core_referer } from '~/stores/core/referer'

const console = new Console('middleware:startup-error')

let hasBeenExecuted = false

export default defineNuxtRouteMiddleware(async (to) => {

  if (hasBeenExecuted)
    return

  hasBeenExecuted = true

  try {
    const startup = useStore_core_startup()
    const infringement = useStore_layout_infringement()
    const authentication = useStore_account_authentication()
    const referer = useStore_core_referer()

    switch (true) {

      case isPolicyInfringementError(startup.error): {

        console.log(
          `policy infringement during startup,`,
          // why? bc. with parallel requests, one can be on error, the other
          // on success with token renewal (thus new session → but precedence
          // rule applyied here, see above comments)
          `session will be destroyed and will be redirected`
        )

        await authentication.logout()

        infringement.$patch({ eventCaptured: true })
        startup.$patch({ error: null })

        /// @NOTE monkey-patch: avoid redirection if anonymous on account pages
        ///     ↳ aim #1: as such anonymous on "dispatch" login page will not
        ///               redirected on login page
        if (isOnAuthenticationPages(to)) {
          console.log(
            `anonymous aleady on an account page,`,
            `no redirection made`
          )
          return
        }

        referer.makeSnapshot(to)

        const newRoute = routeForAnonymous()

        return navigateTo({ path: newRoute })
      }

      case startup.error: {

        console.error(
          `miscellaneous error during startup`,
          `should NEVER happen as it will completely break the user experience`
        )

        return showError(startup.error)
      }

      default: return noop()
    }
  } catch (err) {

    console.error(
      `fatal error while handling an error on startup`,
      `should NEVER happen as it will completely break the user experience`,
      err
    )

    return showError(toString(err))
  }
})
